import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/apiClient';
import type { LoginHistoryResponse } from '../types';

const PAGE_SIZE = 50;

/**
 * Auth gateway expects Feathers-style query params: $limit and $skip (required for pagination).
 * Using Feathers $sort parameter to sort by created date descending.
 */
const buildLoginHistoryUrl = (uuid: string, page: number): string => {
  const params = new URLSearchParams({
    userUuid: uuid,
    $limit: PAGE_SIZE.toString(),
    $skip: (page * PAGE_SIZE).toString(),
    '$sort[created]': '-1', // Feathers format for descending sort
  });
  return `api/auth/users-logins?${params.toString()}`;
};

const fetchLoginHistoryPage = async (uuid: string, page: number): Promise<LoginHistoryResponse> => {
  const response = await apiClient().get(buildLoginHistoryUrl(uuid, page)).json<LoginHistoryResponse>();
  const data = Array.isArray(response?.data) ? response.data : [];
  const total = typeof response?.total === 'number' ? response.total : data.length;
  const limit = typeof response?.limit === 'number' ? response.limit : PAGE_SIZE;
  const skip = typeof response?.skip === 'number' ? response.skip : page * PAGE_SIZE;

  // Data should already be sorted by the API if $sort parameter is supported
  return { total, limit, skip, data };
};

/**
 * Fetches paginated login history for a user with eager loading of next page.
 * @param uuid - User UUID
 * @param page - Current page number (0-indexed)
 */
export const useLoginHistoryPaginatedQuery = (uuid: string | undefined, page = 0) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['loginHistory', uuid, page],
    queryFn: () => fetchLoginHistoryPage(uuid!, page),
    enabled: !!uuid,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Prefetch next page when we're on any page and there might be more data
  const hasNextPage = query.data ? query.data.skip + query.data.data.length < query.data.total : false;

  // Eager load next page
  if (hasNextPage && query.data) {
    queryClient.prefetchQuery({
      queryKey: ['loginHistory', uuid, page + 1],
      queryFn: () => fetchLoginHistoryPage(uuid!, page + 1),
      staleTime: 2 * 60 * 1000,
    });
  }

  return {
    ...query,
    hasNextPage,
    hasPreviousPage: page > 0,
    totalPages: query.data ? Math.ceil(query.data.total / PAGE_SIZE) : 0,
  };
};
